/**
 * DcmCaseOverviewPanel — Disaster Case Management caseload overview (TRUS-538).
 *
 * The admin view of the whole caseload: headline counts, aging, the four
 * breakdown axes the ticket asks for (status / household type / disaster /
 * primary unmet need), and per-case-manager load.
 *
 * On the form choices, since they are deliberate rather than default:
 *
 *  - The headline numbers are STAT TILES, not a chart. A handful of current
 *    values is a KPI row; a grouped bar chart of them would be harder to read.
 *  - Each breakdown is a RANKED HORIZONTAL BAR LIST — the job is "compare
 *    magnitude across a handful of named classes", which is a bar. One measure
 *    (case count) means ONE hue, not a categorical palette: identity is carried
 *    by the row label, which is always visible, so there is no legend and no
 *    color-matching. The bar hue (#3b82f6) is validated against both the light
 *    and dark chart surfaces.
 *  - Axes with many classes (primary unmet need has 18 possible values) fold
 *    their tail into "Other" rather than growing the list — past ~8 classes a
 *    ranked list stops being scannable.
 *  - Per-case-manager load is a TABLE. It is four measures per row, which no
 *    single chart shows honestly.
 *
 * Every number is rendered as text next to its bar, so nothing here depends on
 * reading a length or a color.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeftRight,
  ClipboardList,
  Clock,
  FileDown,
  Flame,
  Home,
  Info,
  Loader2,
  RefreshCw,
  UserCheck,
  Users,
} from 'lucide-react';

import {
  dcmDashboardAPI,
  DcmBreakdownRow,
  DcmFilters,
  DcmOverview,
} from '@/services/api/dcmDashboard';

// The one categorical hue. Validated on both surfaces (light #fcfcfb and dark
// #1a1a19): lightness band, chroma floor and >= 3:1 contrast all pass, so the
// same step works in both modes and does not need a dark-mode variant.
const BAR_HUE = '#3b82f6';

// Past this many classes a ranked list stops being scannable; the tail folds
// into a single "Other" row that keeps the total honest.
const MAX_BREAKDOWN_ROWS = 8;

const nf = new Intl.NumberFormat();

const fmtCount = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : nf.format(n));

const fmtDays = (n: number | null | undefined): string =>
  n === null || n === undefined ? '—' : `${nf.format(Math.round(n))}d`;

/** Turn a raw stored value into something readable without hiding it. */
const humanize = (key: string): string => {
  if (!key) return 'Unknown';
  if (key === key.toUpperCase() && key.includes('_')) {
    return key
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return key.charAt(0).toUpperCase() + key.slice(1);
};

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

const StatTile: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon?: React.ReactNode;
  /** Draws attention without using a data color — for a real warning only. */
  attention?: boolean;
}> = ({ label, value, hint, icon, attention }) => (
  <div
    className={`rounded-xl border p-3 md:p-4 shadow-sm ${
      attention
        ? 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30'
        : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
    }`}
  >
    <div className="mb-1 flex items-center gap-2">
      {icon}
      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 md:text-sm">{label}</span>
    </div>
    <p className="text-xl font-bold text-gray-900 dark:text-gray-100 md:text-2xl">{value}</p>
    {hint && <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{hint}</p>}
  </div>
);

/**
 * A ranked bar list. One hue, value at the tip, no legend — the row label is
 * the identity channel.
 *
 * Bars are 10px thick with a 4px rounded data-end and a square baseline, and
 * are scaled against the largest value in the list rather than the total, so a
 * long tail stays visible instead of collapsing to slivers.
 */
const BreakdownBars: React.FC<{
  title: string;
  icon: React.ReactNode;
  rows: DcmBreakdownRow[];
  emptyText: string;
  /** Shown when the axis has no configured source, so "Unknown" reads correctly. */
  sourceNote?: string;
}> = ({ title, icon, rows, emptyText, sourceNote }) => {
  const display = useMemo(() => {
    const sorted = [...rows].sort((a, b) => b.open_count - a.open_count || b.total_count - a.total_count);
    if (sorted.length <= MAX_BREAKDOWN_ROWS) return sorted;
    const head = sorted.slice(0, MAX_BREAKDOWN_ROWS - 1);
    const tail = sorted.slice(MAX_BREAKDOWN_ROWS - 1);
    return [
      ...head,
      {
        key: `Other (${tail.length} more)`,
        open_count: tail.reduce((s, r) => s + r.open_count, 0),
        closed_count: tail.reduce((s, r) => s + r.closed_count, 0),
        total_count: tail.reduce((s, r) => s + r.total_count, 0),
        avg_days_open: null,
      } as DcmBreakdownRow,
    ];
  }, [rows]);

  const max = useMemo(() => Math.max(1, ...display.map((r) => r.open_count)), [display]);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {icon}
        {title}
      </h3>
      {sourceNote && <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{sourceNote}</p>}

      {display.length === 0 ? (
        <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">{emptyText}</p>
      ) : (
        <ul className="space-y-2.5">
          {display.map((row) => (
            <li key={row.key}>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <span className="truncate text-sm text-gray-700 dark:text-gray-300" title={humanize(row.key)}>
                  {humanize(row.key)}
                </span>
                <span className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                  {fmtCount(row.open_count)}
                  {row.closed_count > 0 && (
                    <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                      open · {fmtCount(row.closed_count)} closed
                    </span>
                  )}
                </span>
              </div>
              <div
                className="h-[10px] w-full overflow-hidden rounded-[2px] bg-gray-100 dark:bg-gray-700/60"
                role="img"
                aria-label={`${humanize(row.key)}: ${row.open_count} open cases of ${row.total_count} total`}
              >
                <div
                  className="h-full rounded-r-[4px]"
                  style={{
                    width: `${Math.max(2, (row.open_count / max) * 100)}%`,
                    backgroundColor: BAR_HUE,
                  }}
                  title={
                    row.avg_days_open !== null
                      ? `${row.open_count} open · avg ${Math.round(row.avg_days_open)} days open`
                      : `${row.open_count} open`
                  }
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export const DcmCaseOverviewPanel: React.FC = () => {
  const [data, setData] = useState<DcmOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);

  const filters: DcmFilters = useMemo(() => ({ open_only: openOnly || undefined }), [openOnly]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    dcmDashboardAPI
      .getOverview(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e?.message || 'Failed to load the case overview');
        setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters]);

  useEffect(() => load(), [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await dcmDashboardAPI.downloadCasesCsv(filters);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading case overview…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { totals, breakdowns, by_dcm: caseload, transfers, sources } = data;
  const statusIsConfigured = sources.case_status === 'custom_field';

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
            <Flame className="h-5 w-5 text-blue-600" />
            Case Overview
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
            Every case in the organization, as of {data.generated_at}.
          </p>
        </div>

        <div className="flex items-center gap-2" data-export-ignore="true">
          <label className="flex cursor-pointer items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={openOnly}
              onChange={(e) => setOpenOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            Open cases only
          </label>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Export CSV
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Headline counts */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Active Cases"
          value={fmtCount(totals.active_cases)}
          hint={`${fmtCount(totals.total_cases)} total`}
          icon={<ClipboardList className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
        />
        <StatTile
          label="Active Households"
          value={fmtCount(totals.active_households)}
          hint={
            totals.cases_without_household > 0
              ? `${fmtCount(totals.cases_without_household)} cases not linked to a household`
              : undefined
          }
          icon={<Home className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
        />
        <StatTile
          label="New Cases"
          value={fmtCount(totals.new_cases_this_week)}
          hint={`this week · ${fmtCount(totals.new_cases_this_month)} this month`}
          icon={<Users className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
        />
        <StatTile
          label="Cases Closed"
          value={fmtCount(totals.closed_this_month)}
          hint={`this month · ${fmtCount(totals.closed_ytd)} YTD`}
          icon={<UserCheck className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
        />
      </div>

      {/* Aging */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile
          label="Average Days Open"
          value={fmtDays(totals.avg_days_open)}
          hint={totals.max_days_open ? `longest ${fmtDays(totals.max_days_open)}` : undefined}
          icon={<Clock className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
        />
        {totals.open_over_days.map((bucket) => (
          <StatTile
            key={bucket.days}
            label={`Open > ${bucket.days} days`}
            value={fmtCount(bucket.count)}
            hint={
              totals.active_cases > 0
                ? `${Math.round((bucket.count / totals.active_cases) * 100)}% of active cases`
                : undefined
            }
            icon={<Clock className="h-4 w-4 text-gray-400 md:h-5 md:w-5" />}
          />
        ))}
      </div>

      {totals.unassigned_open_cases > 0 && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile
            label="Unassigned Open Cases"
            value={fmtCount(totals.unassigned_open_cases)}
            hint="no case manager"
            attention
            icon={<AlertTriangle className="h-4 w-4 text-amber-600 md:h-5 md:w-5" />}
          />
        </div>
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BreakdownBars
          title="Cases by Status"
          icon={<ClipboardList className="h-4 w-4 text-gray-400" />}
          rows={breakdowns.by_status}
          emptyText="No cases match the current filters."
          sourceNote={
            statusIsConfigured
              ? undefined
              : 'Showing enrollment status. Configure a case-status field to use your own vocabulary.'
          }
        />
        <BreakdownBars
          title="Primary Unmet Need"
          icon={<AlertTriangle className="h-4 w-4 text-gray-400" />}
          rows={breakdowns.by_primary_need}
          emptyText="No needs recorded."
          sourceNote={
            sources.primary_need === 'household_member_needs'
              ? 'Most urgent unresolved need on the household.'
              : undefined
          }
        />
        <BreakdownBars
          title="Household Type"
          icon={<Home className="h-4 w-4 text-gray-400" />}
          rows={breakdowns.by_household_type}
          emptyText="No household types recorded."
          sourceNote={
            totals.cases_without_household > 0
              ? `${fmtCount(totals.cases_without_household)} cases have no linked household and show as Unknown.`
              : undefined
          }
        />
        <BreakdownBars
          title="Disaster"
          icon={<Flame className="h-4 w-4 text-gray-400" />}
          rows={breakdowns.by_disaster}
          emptyText="No disasters recorded."
        />
      </div>

      {/* Per-case-manager load — a table, because it is four measures per row. */}
      <section className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <h3 className="flex items-center gap-2 border-b border-gray-100 p-4 text-sm font-semibold text-gray-900 dark:border-gray-700 dark:text-gray-100">
          <Users className="h-4 w-4 text-gray-400" />
          Caseload by Case Manager
        </h3>
        {caseload.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">No cases are assigned yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                  <th className="px-4 py-2 font-medium">Case Manager</th>
                  <th className="px-4 py-2 text-right font-medium">Open</th>
                  <th className="px-4 py-2 text-right font-medium">Closed</th>
                  <th className="px-4 py-2 text-right font-medium">Avg Days Open</th>
                  <th className="px-4 py-2 text-right font-medium">Longest</th>
                  <th className="px-4 py-2 text-right font-medium">Aging</th>
                </tr>
              </thead>
              <tbody>
                {caseload.map((row) => (
                  <tr
                    key={row.user_id ?? 'unassigned'}
                    className="border-b border-gray-50 last:border-0 dark:border-gray-700/50"
                  >
                    <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                      {row.name}
                      {row.user_id === null && (
                        <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                          <AlertTriangle className="h-3 w-3" />
                          Unassigned
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100">
                      {fmtCount(row.open_cases)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {fmtCount(row.closed_cases)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {fmtDays(row.avg_days_open)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {fmtDays(row.max_days_open)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                      {fmtCount(row.stale_cases)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Transfers — shown with their caveats, because they are an approximation. */}
      <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <ArrowLeftRight className="h-4 w-4 text-gray-400" />
          Case Transfers
        </h3>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatTile label="Transferred In" value={fmtCount(transfers.transferred_in)} />
          <StatTile label="Transferred Out" value={fmtCount(transfers.transferred_out)} />
          <StatTile label="Cases Affected" value={fmtCount(transfers.cases_transferred)} />
          <StatTile
            label="Unknown Origin"
            value={fmtCount(transfers.unknown_origin)}
            hint="no earlier record"
          />
        </div>
        {transfers.caveats?.length > 0 && (
          <details className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-400">
              <Info className="h-3.5 w-3.5" />
              How these numbers are derived
            </summary>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-gray-600 dark:text-gray-400">
              {transfers.caveats.map((caveat) => (
                <li key={caveat}>{caveat}</li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
};

export default DcmCaseOverviewPanel;
